import classNames from "classnames"
import React, { useRef, useMemo } from "react"
import SwiperCore, { Navigation, Pagination, Controller } from "swiper"
import { Swiper } from "swiper/react"

import SwiperSlideContent from "./ComparisonTable/SwiperSlideContent"
import SwiperSlideHeaders from "./ComparisonTable/SwiperSlideHeaders"
import { extractFromTag } from "./ComparisonTable/utils"
import { Color } from "../../../constants/V2/color"
import ArrowIcon from "../../elements/V2/Icons/ArrowIcon"
import Tooltip from "../../elements/V2/Tooltip"
import Typography from "../../elements/V2/Typography"

import { TableRegex } from "@constants/tables"
import { usePageLayoutState } from "@utils/layout"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"

SwiperCore.use([Navigation, Pagination, Controller])

const CELL_CLASSES =
  "text-center flex items-center px-16 py-8 h-48 md:h-56 lg:h-[68px] rounded-8"
const CELL_TEXT_CLASSSES =
  "[display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] line-clamp-2 overflow-hidden text-ellipsis"
type Props = {
  sectionId: string
  numDesktopColumns?: 3 | 4 | 6
  columnImages?: string[]
  rowHeadings: string[]
  data: string[][]
  backgroundColor: Color.Black | Color.Charcoal | Color.White | Color.MintGreen
  outerContainer: "gridSection" | "tabSelector"
}

const SingleEntryComparisonTable = ({
  sectionId,
  numDesktopColumns = 4,
  columnImages,
  rowHeadings,
  data,
  backgroundColor,
  outerContainer,
}: Props) => {
  const { hasAnchorNavigation } = usePageLayoutState()
  const tableTheme =
    backgroundColor === Color.Black || backgroundColor === Color.Charcoal
      ? "dark"
      : "light"
  const headerSwiperRef = useRef<SwiperCore | null>(null)
  const titleRowIndices = useRef<number[]>([])
  const columnCount = useMemo(() => {
    let longestDataRow = 0
    data.forEach((dataRow) => {
      if (dataRow.length > longestDataRow) {
        longestDataRow = dataRow.length
      }
    })
    return longestDataRow
  }, [data])

  return (
    <div className="grid grid-cols-2 gap-x-16 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] md:gap-x-20 lg:gap-x-32">
      {columnCount <= numDesktopColumns && (
        <div className="sticky top-[82px] z-20 col-span-2 flex items-center justify-between sm:hidden">
          <button
            type="button"
            className={classNames(
              `swiper-prev-${sectionId}`,
              "relative my-8 rotate-180 disabled:pointer-events-none disabled:opacity-50",
              tableTheme === "light" ? "text-charcoal" : "text-white"
            )}
          >
            <ArrowIcon animateOnHover={false} />
          </button>
          <div
            className={classNames(
              `swiper-pagination-${sectionId}`,
              "!relative flex items-center px-16 py-8"
            )}
          />
          <button
            type="button"
            className={classNames(
              `swiper-next-${sectionId}`,
              "my-8 disabled:pointer-events-none disabled:opacity-50",
              tableTheme === "light" ? "text-charcoal" : "text-white"
            )}
          >
            <ArrowIcon animateOnHover={false} />
          </button>
        </div>
      )}
      <div className="relative z-10 row-span-2">
        <div
          className={classNames(
            getBackgroundColorClass(backgroundColor),
            "sticky rounded-b-8", // top positioning scales with navbar height
            hasAnchorNavigation || outerContainer === "gridSection"
              ? "top-[69px]"
              : "top-[58px] sm:top-60 lg:top-[62px]"
          )}
        >
          <div
            className={classNames("flex items-end", {
              "h-[92px] lg:h-[128px]":
                columnImages && columnImages.length !== 0,
            })}
          >
            {columnCount > numDesktopColumns && (
              <div
                className={classNames(
                  `swiper-pagination-${sectionId}`,
                  "px-16 py-8"
                )}
              />
            )}
          </div>
          <div className={CELL_CLASSES}>
            <Tooltip
              text={
                rowHeadings[0].match(TableRegex.TITLE)
                  ? extractFromTag(rowHeadings[0], "title")
                  : rowHeadings[0]
              }
              backgroundColor={
                tableTheme === "light" ? Color.Charcoal : Color.White
              }
              showTooltipCondition="hoverContentEllipsed"
            >
              <Typography
                font="grotesk"
                weight="medium"
                color={getAccentColor(
                  tableTheme === "light" ? Color.White : Color.Charcoal
                )}
                size={
                  rowHeadings[0].match(TableRegex.TITLE) ? "body-lg" : "body-md"
                }
                text={
                  rowHeadings[0].match(TableRegex.TITLE)
                    ? extractFromTag(rowHeadings[0], "title")
                    : rowHeadings[0]
                }
                className={classNames(CELL_TEXT_CLASSSES, "text-left")}
              />
            </Tooltip>
          </div>
        </div>
        {rowHeadings.map((rowHeading, index) => {
          if (rowHeading.match(TableRegex.TITLE)) {
            titleRowIndices.current.push(index)
          }
          return (
            index !== 0 && (
              <div
                key={`${rowHeading.replace(" ", "-")}-${index}`}
                className={classNames(
                  CELL_CLASSES,
                  {
                    "mt-16 md:mt-24": rowHeading.match(TableRegex.TITLE),
                  },
                  tableTheme === "light"
                    ? "even:bg-light-gray"
                    : backgroundColor === Color.Charcoal
                      ? "even:bg-black"
                      : "even:bg-charcoal"
                )}
              >
                <Tooltip
                  text={
                    rowHeading.match(TableRegex.TITLE)
                      ? extractFromTag(rowHeading, "title")
                      : rowHeading
                  }
                  backgroundColor={
                    tableTheme === "light" ? Color.Charcoal : Color.White
                  }
                  showTooltipCondition="hoverContentEllipsed"
                >
                  <Typography
                    font="grotesk"
                    weight="medium"
                    color={getAccentColor(
                      tableTheme === "light" ? Color.White : Color.Charcoal
                    )}
                    size={
                      rowHeading.match(TableRegex.TITLE) ? "body-lg" : "body-md"
                    }
                    text={
                      rowHeading.match(TableRegex.TITLE)
                        ? extractFromTag(rowHeading, "title")
                        : rowHeading
                    }
                    className={classNames(CELL_TEXT_CLASSSES, "text-left")}
                  />
                </Tooltip>
              </div>
            )
          )
        })}
      </div>
      <div
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          "sticky z-10 rounded-b-8", // top positioning scales with navbar height
          hasAnchorNavigation || outerContainer === "gridSection"
            ? "top-[69px]"
            : "top-[58px] sm:top-60 lg:top-[62px]"
        )}
      >
        <Swiper
          id={`heading-swiper-${sectionId}`}
          noSwiping={false}
          spaceBetween={16}
          allowTouchMove={false}
          breakpoints={{
            667: {
              spaceBetween: 0,
              slidesPerView:
                columnCount < numDesktopColumns
                  ? columnCount
                  : numDesktopColumns,
              noSwiping: columnCount > numDesktopColumns ? false : true,
            },
          }}
          navigation={{
            nextEl: `.swiper-next-${sectionId}`,
            prevEl: `.swiper-prev-${sectionId}`,
          }}
          onBeforeInit={(swiper) => {
            headerSwiperRef.current = swiper
          }}
        >
          {SwiperSlideHeaders({
            data,
            columnImages,
            columnCount,
            numDesktopColumns,
            tableTheme,
            backgroundColor,
            titleRowIndices,
          })}
        </Swiper>
      </div>
      <div className="z-1">
        <Swiper
          id={`data-swiper-${sectionId}`}
          noSwiping={false}
          spaceBetween={16}
          pagination={{
            el: `.swiper-pagination-${sectionId}`,
            clickable: true,
            renderBullet: function (_, className) {
              return `<span class="${classNames(
                className,
                "!h-4 !w-4 !mr-4",
                tableTheme === "light" ? "!bg-charcoal" : "!bg-white"
              )}"></span>`
            },
          }}
          breakpoints={{
            667: {
              spaceBetween: 0,
              slidesPerView:
                columnCount < numDesktopColumns
                  ? columnCount
                  : numDesktopColumns,
              noSwiping: columnCount > numDesktopColumns ? false : true,
            },
          }}
          grabCursor
          navigation={{
            nextEl: `.swiper-next-${sectionId}`,
            prevEl: `.swiper-prev-${sectionId}`,
          }}
          onInit={(swiper) => {
            if (headerSwiperRef.current) {
              swiper.controller.control = headerSwiperRef.current
            }
          }}
        >
          {SwiperSlideContent({
            data,
            columnCount,
            tableTheme,
            backgroundColor,
            titleRowIndices,
          })}
        </Swiper>
      </div>
    </div>
  )
}

export default SingleEntryComparisonTable
